import { useState } from 'react';
import type { ReactElement } from 'react';
import { fileUploadMessage } from 'src/components/FileUploadForm/fileUploadMessage/fileUploadMessage';
import ModalImg from 'src/assets/images/create-menu-item-modal.png';
import { StatusBar, SnackbarStatus } from '@weareopenr/component-library';
import { importCSV } from 'src/api/importCSV/importCSV';
import { useInventory } from 'src/hooks/useInventory';
import { ModalWrapper } from 'src/components';
import FileUploadForm from 'src/components/FileUploadForm';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import styles from './InventoryImportModal.module.scss';

export interface InventoryImportModalProps {
  closeModal: () => void;
  open: boolean;
}

const InventoryImportModal = ({ closeModal, open }: InventoryImportModalProps): ReactElement => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { refetch: refetchInventory } = useInventory();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent, captureException } = useUserTracking();

  const handleSubmit = ({ files }: { files: File }) => {
    if (!files) return;

    clearError();
    const formData: FormData = new FormData();
    formData.append('recipesFile', files);
    formData.append('source', '6d1dcbe2-cb6b-44fe-bc31-3a3d961663d0');
    setIsLoading(true);

    importCSV(formData)
      .then((res) => res.json())
      .then((res) => {
        if (res.Error) {
          showNotification({
            status: SnackbarStatus.ERROR,
            message: res.Error,
          });
          setIsLoading(false);
        } else {
          trackEvent('inventory_imported', { added: res?.added, updated: res?.updated });
          showNotification({
            message: fileUploadMessage('inventory item', res),
            status: SnackbarStatus.SUCCESS,
          });
          refetchInventory();
          closeModal();
          setIsLoading(false);
        }
      })
      .catch((err) => {
        captureException(err, { tags: { action: 'import_inventory' } });
        showNotification({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' });
        setIsLoading(false);
      });
  };

  return (
    <ModalWrapper closeModal={closeModal} open={open} image={ModalImg} imageAlt="Picture of ingredients">
      <>
        <FileUploadForm
          formTitle="Import inventory"
          formMessage="Select CSV for upload"
          isLoading={isLoading}
          onFormSubmit={handleSubmit}
        />
        <div className={styles['error-banner']}>
          {errorMessage && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </>
    </ModalWrapper>
  );
};

export default InventoryImportModal;
